/*
 * Pagination — composant partagé extrait de `Patient/index.tsx` (2026-05-05).
 *
 * Aligne **toutes** les listes paginées de l'app sur le même rendu :
 * compteur "Affichage de N sur M" à gauche, boutons précédent/suivant +
 * indicateur "Page X / Y" à droite.
 *
 * Convention back : pages sont 0-indexées dans la réponse `PageMapper.toMap`
 * (cf. CLAUDE.md §Endpoint Consultations / Invoices). Le composant affiche
 * `currentPage + 1` à l'utilisateur ; le `onPageChange` reçoit la valeur
 * 0-indexée à passer telle quelle au back.
 *
 * Quand l'afficher :
 *   {totalPages > 1 && <Pagination .../>}
 * Au-delà de zéro résultat dans une page unique, masquer le bloc — c'est
 * le pattern actuel sur Patient et il évite un compteur "Page 1/1" inutile.
 */
import * as React from "react";

export type PaginationProps = {
  /** Page courante (0-indexée — comme renvoyée par PageMapper.toMap). */
  currentPage: number;
  /** Nombre total de pages. Si ≤ 1, le composant ne s'affiche pas. */
  totalPages: number;
  /** Nombre total d'éléments (toutes pages confondues). */
  totalElements: number;
  /** Nombre d'éléments visibles sur la page courante (souvent `data.length`). */
  currentCount: number;
  hasNext: boolean;
  hasPrevious: boolean;
  /** Callback page change (reçoit la nouvelle page 0-indexée). */
  onPageChange: (newPage: number) => void;
  /**
   * Label singulier de l'item ("patient", "consultation", "facture"...).
   * Le composant gère lui-même le pluriel "{label}s" basé sur totalElements.
   */
  itemLabel: string;
  /** Désactive les boutons (pendant un fetch en cours par ex.). */
  isLoading?: boolean;
  /** Optionnel : scroll-to-top après page change. Default true. */
  scrollToTop?: boolean;
};

export function Pagination({
  currentPage,
  totalPages,
  totalElements,
  currentCount,
  hasNext,
  hasPrevious,
  onPageChange,
  itemLabel,
  isLoading = false,
  scrollToTop = true,
}: PaginationProps) {
  // Convention historique : ne rien rendre si une seule page suffit.
  // Le caller peut toujours forcer l'affichage en passant totalPages=2
  // mais c'est extrêmement rare — pas de prop pour l'override.
  if (totalPages <= 1) return null;

  const handleChange = (newPage: number) => {
    onPageChange(newPage);
    if (scrollToTop) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const pluralS = totalElements > 1 ? "s" : "";

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between animate-in fade-in duration-500 fill-mode-both">
      <div className="text-sm text-slate-500">
        Affichage de{" "}
        <span className="font-semibold text-slate-700">{currentCount}</span>{" "}
        sur{" "}
        <span className="font-semibold text-slate-700">{totalElements}</span>{" "}
        {itemLabel}
        {pluralS}
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => handleChange(currentPage - 1)}
          disabled={!hasPrevious || isLoading}
          aria-label="Page précédente"
          className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-4 focus:ring-emerald-500/10"
        >
          <i className="bx bx-chevron-left text-xl" />
        </button>
        <span className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600">
          Page{" "}
          <span className="font-semibold text-slate-800">
            {currentPage + 1}
          </span>{" "}
          / {totalPages}
        </span>
        <button
          onClick={() => handleChange(currentPage + 1)}
          disabled={!hasNext || isLoading}
          aria-label="Page suivante"
          className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-40 focus:outline-none focus:ring-4 focus:ring-emerald-500/10"
        >
          <i className="bx bx-chevron-right text-xl" />
        </button>
      </div>
    </div>
  );
}
